// @aiContributed-MAPFuse-2026-01-08
import React from 'react';
import _ from 'underscore';
import BluebirdPromise from 'bluebird';
// eslint-disable-next-line import/no-unresolved
import { reactAdExtensionEditorAdapter } from '@bingads-webui-campaign-react/react-adextension-editor-adapter';

// Helper to wrap component with additional props - matching ui-next pattern
const withProps = (WrappedComponent, extraProps) => {
  const HoC = props => (
    <WrappedComponent {..._.extend({}, props, extraProps)} />
  );
  return HoC;
};

// Dynamic feature registry with all extension types support
const dynamicFeatures = {
  promotion: props => import(/* webpackChunkName: "ad-extensions-promotion" */'../../feature-bootstrap/ad-extensions-feature-promotion')
    .then(({ default: theFeaturePromise }) => theFeaturePromise)
    .then(({
      PromotionExtensionEditor,
      universalItemTemplate,
    }) => ({
      // Note reactAdExtensionEditorAdapter returns a Backbone.View, so it must be the top function
      DynamicPromotionExtensionEditor: reactAdExtensionEditorAdapter(withProps(PromotionExtensionEditor, props)),
      universalItemTemplate,
    })),
  filterlink: props => import(/* webpackChunkName: "ad-extensions-filterlink" */'../../feature-bootstrap/ad-extensions-feature-filter-link')
    .then(({ default: theFeaturePromise }) => theFeaturePromise)
    .then(({
      FilterLinkExtensionEditor,
      universalItemTemplate,
      repositoryGetODataErrors,
    }) => ({
      // Note reactAdExtensionEditorAdapter returns a Backbone.View, so it must be the top function
      DynamicFilterLinkExtensionEditor: reactAdExtensionEditorAdapter(withProps(FilterLinkExtensionEditor, props)),
      universalItemTemplate,
      hideHelpTooltip: true,
      repositoryOptions: {
        getODataErrors: _.partial(repositoryGetODataErrors, _, props.i18n),
      },
    })),
  flyer: props => import(/* webpackChunkName: "ad-extensions-flyer" */'../../feature-bootstrap/ad-extensions-feature-flyer')
    .then(({ default: theFeaturePromise }) => theFeaturePromise)
    .then(({
      FlyerExtensionEditor,
      universalItemTemplate,
    }) => ({
      // Note reactAdExtensionEditorAdapter returns a Backbone.View, so it must be the top function
      DynamicFlyerExtensionEditor: reactAdExtensionEditorAdapter(withProps(FlyerExtensionEditor, props)),
      universalItemTemplate,
      hideHelpTooltip: true,
    })),
  video: props => import(/* webpackChunkName: "ad-extensions-video" */'../../feature-bootstrap/ad-extensions-feature-video')
    .then(({ default: theFeaturePromise }) => theFeaturePromise)
    .then(({
      VideoExtensionEditor,
      VideoExtensionEditorV2,
      universalItemTemplate,
      repositoryGetODataErrors,
    }) => ({
      // Note reactAdExtensionEditorAdapter returns a Backbone.View, so it must be the top function
      DynamicVideoExtensionEditor: reactAdExtensionEditorAdapter(withProps(
        props.permissions.IsVideoAdExtensionsVideoManagementEnabled ? VideoExtensionEditorV2 : VideoExtensionEditor,
        props
      )),
      universalItemTemplate,
      hideHelpTooltip: true,
      repositoryOptions: {
        getODataErrors: _.partial(repositoryGetODataErrors, _, props.i18n),
      },
    })),
  leadform: props => import(/* webpackChunkName: "ad-extensions-leadform" */'../../feature-bootstrap/ad-extensions-feature-leadform')
    .then(({ default: theFeaturePromise }) => theFeaturePromise)
    .then(({
      LeadFormExtensionEditor,
      universalItemTemplate,
      columnItemTemplate,
      repositoryGetODataErrors,
    }) => ({
      // Note reactAdExtensionEditorAdapter returns a Backbone.View, so it must be the top function
      DynamicLeadFormExtensionEditor: reactAdExtensionEditorAdapter(withProps(LeadFormExtensionEditor, props)),
      universalItemTemplate,
      columnItemTemplate,
      hideHelpTooltip: true,
      repositoryOptions: {
        getODataErrors: _.partial(repositoryGetODataErrors, _, props.i18n),
      },
    })),
  businessinfo: props => import(/* webpackChunkName: "ad-extensions-businessinfo" */'../../feature-bootstrap/ad-extensions-feature-business-info')
    .then(({ default: theFeaturePromise }) => theFeaturePromise)
    .then(({
      BusinessInfoExtensionEditor,
      universalItemTemplate,
    }) => ({
      // Note reactAdExtensionEditorAdapter returns a Backbone.View, so it must be the top function
      DynamicBusinessInfoExtensionEditor: reactAdExtensionEditorAdapter(withProps(BusinessInfoExtensionEditor, props)),
      universalItemTemplate,
      hideHelpTooltip: true,
    })),
};

// Provides HOC that injects adExtensionsDynamicFeaturesLoader prop.
// The loader returns a Promise resolving to a resolver function by extension type.
export const withAllDynamicFeatures = (Component, propName = 'adExtensionsDynamicFeaturesLoader') => withProps(Component, {
  [propName]: props =>
    BluebirdPromise.props(_.mapObject(dynamicFeatures, loader => loader(props)))
      .then(resolvedDynamicFeature => type => resolvedDynamicFeature[type.toLowerCase()]),
});

export default {
  withAllDynamicFeatures,
};
